import { PAGE_TYPES } from '../../../enums/page-types';
import { formatPrice } from '../../../services/formatPrice';
import hasStock from '../../../lib/hasStock';

/**
 * Generate the pay-button label for the current checkout.
 *
 * The verb + amount are driven by the SELECTED price (multi-price aware):
 *   - no price selected (multi-price)      → bare verb ("Pay")
 *   - one-time selected                    → "Pay $X" (bare "Pay" when total due is $0)
 *   - subscription/payment-plan selected   → always bare "Subscribe" (never an amount)
 * "$0" means the resolved TOTAL DUE (setup fees / variants / discounts included).
 * Falls back to the scalar product.type for legacy / single-price products.
 *
 * @param {object} opts
 * @param {boolean}  opts.showPriceOnButton - one-time only; has no effect on subscriptions
 * @param {object}   opts.page
 * @param {object}   opts.product         - full product object (for type / stock / variants checks)
 * @param {object}   [opts.selectedPrice] - resolved price subdoc from product.prices.
 *                                          Drives the verb (billingType) + currency.
 * @param {string}   opts.locale
 * @param {number}   opts.totalDueAmount
 * @param {object}   opts.labels
 */
export default function getPayButtonText({
  showPriceOnButton,
  page,
  product,
  selectedPrice,
  locale,
  totalDueAmount,
  labels,
}) {
  // Prefer selected-price currency, fall back to product-level currency
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.currency)
  const currency = selectedPrice?.currency || product.currency;

  if (page.type === PAGE_TYPES.FORM) {
    return labels.submitButtonText;
  }

  if (page.type === PAGE_TYPES.CHECKOUT) {
    if (!hasStock(product)) {
      return labels.soldOutButtonText;
    }

    // No price chosen yet on a multi-price product → generic verb, no amount
    // (billing type is unknown until the customer selects a price).
    const enabledPriceCount = product.prices?.filter(p => p.enabled !== false).length ?? 0;
    if (enabledPriceCount > 1 && !selectedPrice) {
      return labels.payButtonText;
    }

    // Verb follows the SELECTED price's billing type, falling back to the
    // scalar product.type for legacy / single-price products.
    // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type)
    const isSubscription = selectedPrice?.billingType
      ? selectedPrice.billingType === 'recurring'
      : product.type === 'subscription';
    const verb = isSubscription ? labels.subscribeButtonText : labels.payButtonText;

    // Subscriptions never show the amount; one-time obeys the theme toggle.
    if (isSubscription || showPriceOnButton === false || totalDueAmount <= 0) {
      return verb;
    }

    return `${verb} ${formatPrice({
      amount: totalDueAmount,
      currency,
      locale: locale,
      style: 'currency',
    })}`;
  }

  if (page.type === PAGE_TYPES.EVENT) {
    if (page.eventDetails?.status === 'canceled') {
      return labels.canceledButtonText;
    }

    if (page.eventDetails?.isSoldOut) {
      return labels.soldOutButtonText;
    }

    if (page.showPriceOnButton === false) {
      return labels.getTicketsButtonText;
    }

    return `${labels.getTicketsButtonText} ${formatPrice({
      amount: totalDueAmount,
      currency: page.eventDetails?.currency,
      locale: locale,
      style: 'currency',
    })}`;
  }

  return labels.payButtonText;
}
